import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilmCard from "@/components/feature/FilmCard";
import StarCard from "@/components/feature/StarCard";
import TopicCard from "@/components/feature/TopicCard";
import EmptyState from "@/components/feature/EmptyState";
import { searchContent } from "@/lib/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const tabs = [
  { key: "all", label: "全部" },
  { key: "films", label: "影视作品" },
  { key: "stars", label: "明星人物" },
  { key: "topics", label: "网络话题" },
];

export default function SearchPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const queryParam = params.get("q") ?? "";

  const [input, setInput] = useState(queryParam);
  const [tab, setTab] = useState("all");

  useDocumentMeta({
    title: queryParam
      ? `${queryParam} - 搜索结果 - 巨乳娱乐资讯网`
      : "搜索 - 巨乳娱乐资讯网",
    description: queryParam
      ? `在巨乳娱乐资讯网中搜索“${queryParam}”，查看相关影视作品、明星人物与网络话题。`
      : "在巨乳娱乐资讯网中搜索影视作品、明星人物与网络话题。",
    keywords: "站内搜索,影视搜索,明星搜索,话题搜索,巨乳娱乐资讯网",
  });

  useEffect(() => {
    setInput(queryParam);
  }, [queryParam]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const value = input.trim();
      if (value === queryParam) return;
      if (value) {
        setParams({ q: value });
      } else {
        setParams({});
      }
    }, 450);
    return () => window.clearTimeout(timer);
  }, [input, queryParam, setParams]);

  const result = useMemo(() => searchContent(queryParam), [queryParam]);

  const total =
    result.films.length + result.stars.length + result.topics.length;

  const counts: Record<string, number> = {
    all: total,
    films: result.films.length,
    stars: result.stars.length,
    topics: result.topics.length,
  };

  const showFilms = tab === "all" || tab === "films";
  const showStars = tab === "all" || tab === "stars";
  const showTopics = tab === "all" || tab === "topics";

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    if (value) setParams({ q: value });
    else setParams({});
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <div className="mt-4 flex flex-col gap-3">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          站内搜索
        </h1>
        <form
          onSubmit={handleSubmit}
          className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2.5 md:max-w-2xl"
        >
          <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="输入关键词，如「星海回声」「林知遥」「国庆档」"
            aria-label="搜索关键词"
            className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400 md:text-base"
          />
          <button
            type="submit"
            className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            搜索
          </button>
        </form>
      </div>

      {queryParam && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">搜索</span>
          <span className="rounded-full bg-accent-100 px-3 py-1 text-sm text-accent-900">
            {queryParam}
          </span>
          <span className="text-sm text-foreground-500">
            共找到 <strong className="text-primary-600">{total}</strong> 条结果
          </span>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2 border-b border-background-200 pb-3">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors cursor-pointer md:text-sm ${
              tab === item.key
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
            }`}
          >
            {item.label}
            <span className="ml-1 opacity-70">{counts[item.key]}</span>
          </button>
        ))}
      </div>

      {!queryParam ? (
        <div className="mt-6">
          <EmptyState
            title="输入关键词开始搜索"
            description="可以搜索影视作品名称、明星人物姓名，或网络话题关键词。"
            actionLabel="浏览影视作品"
            onAction={() => navigate("/category/films")}
          />
        </div>
      ) : total === 0 ? (
        <div className="mt-6">
          <EmptyState
            title="未找到相关内容，您可修改关键词重试"
            description={`没有与“${queryParam}”匹配的影视作品、明星人物或网络话题，换个关键词试试看。`}
            actionLabel="返回首页"
            onAction={() => navigate("/")}
          />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-10">
          {showFilms && result.films.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="inline-block h-5 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
                影视作品
                <span className="text-sm font-normal text-foreground-500">
                  {result.films.length}
                </span>
              </h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {result.films.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>
            </section>
          )}

          {showStars && result.stars.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="inline-block h-5 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
                明星人物
                <span className="text-sm font-normal text-foreground-500">
                  {result.stars.length}
                </span>
              </h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {result.stars.map((star) => (
                  <StarCard key={star.id} star={star} />
                ))}
              </div>
            </section>
          )}

          {showTopics && result.topics.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="inline-block h-5 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
                网络话题
                <span className="text-sm font-normal text-foreground-500">
                  {result.topics.length}
                </span>
              </h2>
              <div className="flex flex-col gap-3">
                {result.topics.map((topic) => (
                  <TopicCard key={topic.id} topic={topic} variant="row" />
                ))}
              </div>
            </section>
          )}

          <div className="flex flex-wrap gap-3">
            <Link
              to="/category/films"
              className="whitespace-nowrap rounded-md border border-background-200 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              浏览全部影视作品
            </Link>
            <button
              type="button"
              onClick={() => {
                setInput("");
                setParams({});
              }}
              className="whitespace-nowrap rounded-md border border-background-200 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
            >
              清空搜索
            </button>
          </div>
        </div>
      )}
    </div>
  );
}